"use client";

import type { LeadFormDeliveryContext } from "@homeinstead/forms/types";
import type { ImageContent, StepOneHero } from "@homeinstead/schemas";
import type { CareSeekerLeadFormResponse } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type { PageBuilderOffice } from "~www/types/office";
import type {
  FormRegularStep,
  FormStep,
  FormThankYou,
  FullscreenTakeoverSettings,
  StepTransitionSettings,
} from "./form-types";
import type { OfficeLeadFormInfo } from "./get-office-lead-form-info";
import type { LeadFormPageZone } from "./LeadFormAnalyticsContext";

import { LEAD_FORM_DELIVERY_CONTEXT } from "@homeinstead/forms/lead-form";
import { logError } from "@homeinstead/shared/logging";
import {
  Suspense,
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { submitCareSeekerShallowForm } from "~www/actions/submitCareSeekerShallowForm";
import { HeroImage } from "~www/components/LeadForm/CareSeeker/shared/HeroImage";
import { CaregiverDrawerWithSearchParams } from "~www/components/PageBuilder/blocks/CaregiverDrawerWithSearchParams";
import { isHoneypotRejection } from "~www/utils/is-honeypot-rejection";
import { buildFormAnswerPlaceholders } from "./build-form-answer-placeholders";
import { stepsHavePostalCodeInput } from "./form-step-utils";
import { getStepUserSelection } from "./get-step-user-selection";
import { KeyboardStepActionContext } from "./keyboard-step-action-context";
import { LeadFormAnalyticsProvider } from "./LeadFormAnalyticsContext";
import { LeadFormForm } from "./LeadFormForm";
import { LeadFormHoneypotField } from "./LeadFormHoneypotField";
import { LeadFormPersistedValuesContext } from "./LeadFormPersistedValuesContext";
import { LeadFormStepSurface } from "./LeadFormStepSurface";
import { LeadFormTimingTokenField } from "./LeadFormTimingTokenField";
import { LeadFormWrapper } from "./LeadFormWrapper";
import { defaultLeadFormStyleClasses } from "./lead-form-styles";
import { normalizeOfficeId } from "./normalize-office-id";
import { PersistedFieldInputs } from "./PersistedFieldInputs";
import { StepRegion } from "./StepRegion";
import { ThankYou } from "./ThankYou";
import { useLeadFormBrowserHistory } from "./use-lead-form-browser-history";
import { useLeadFormKeyboardSubmit } from "./use-lead-form-keyboard-submit";
import { useLeadFormMachine } from "./use-lead-form-machine";
import { useLeadFormTakeover } from "./use-lead-form-takeover";
import { useStepStyles } from "./use-step-styles";
import { useStepTransition } from "./use-step-transition";
import { useLeadFormSubmissionTracking } from "./useLeadFormSubmissionTracking";

type LeadFormProps = {
  steps: FormStep[];
  thankYou?: FormThankYou | null;
  deliveryContext?: LeadFormDeliveryContext | null;
  franchiseId: number;
  leadSource?: string;
  formVariant?: string;
  officeLeadFormInfo: OfficeLeadFormInfo;
  office?: PageBuilderOffice;
  logComponentName?: string;
  /** Segment page_zone value. Defaults to "sitebuilder". */
  pageZone?: LeadFormPageZone;
  /** Sanity careLeadForm versionId — sent as form_version_id in analytics events. */
  analyticsVersionId?: string;
  /** Per-block hero image override for the desktop viewport. */
  heroImageLarge?: ImageContent | null;
  /** Per-block hero image override for the mobile/tablet viewport. */
  heroImageSmall?: ImageContent | null;
  /**
   * Lead form block layout gate. `buttonStack` renders the first regular
   * step through ButtonStackHeroStep.
   */
  stepOneHero?: StepOneHero | null;
  /** Form-level step transition settings from the Sanity leadForm document. */
  stepTransition?: StepTransitionSettings;
  /** Form-level fullscreen takeover settings from the Sanity leadForm document. */
  fullscreenTakeover?: FullscreenTakeoverSettings;
};

function LeadForm({
  steps,
  thankYou,
  deliveryContext,
  franchiseId,
  leadSource = "fo-home-page",
  formVariant = "lfb",
  officeLeadFormInfo,
  office,
  logComponentName = "LeadForm",
  pageZone = "sitebuilder",
  analyticsVersionId,
  heroImageLarge,
  heroImageSmall,
  stepOneHero,
  stepTransition,
  fullscreenTakeover,
}: LeadFormProps) {
  const styleClasses = defaultLeadFormStyleClasses;
  const [state, submitFormAction, isPending] = useActionState<
    LeadResponse<CareSeekerLeadFormResponse> | null,
    FormData
  >(submitCareSeekerShallowForm, null);

  const [isTimingTokenReady, setIsTimingTokenReady] = useState(true);
  const onTimingTokenReadyChange = useCallback((isReady: boolean) => {
    setIsTimingTokenReady(isReady);
  }, []);

  const {
    placeholderValues,
    phoneNumber,
    officeRating,
    postalCodeFromOffice,
    locationFromOffice,
    isOfficeFormAndPostalCodeMissing,
  } = officeLeadFormInfo;

  const initialContext = useMemo(() => {
    const hasRuntimePostalLocation =
      deliveryContext === LEAD_FORM_DELIVERY_CONTEXT.officeLocal;

    if (!hasRuntimePostalLocation && !stepsHavePostalCodeInput(steps)) {
      return undefined;
    }

    return {
      postalLocationValues: {
        location: locationFromOffice || postalCodeFromOffice || "",
        postalCode: postalCodeFromOffice || "",
      },
    };
  }, [deliveryContext, steps, locationFromOffice, postalCodeFromOffice]);

  const {
    currentStep,
    currentStepIndex,
    errors,
    isHeroMode,
    isLastStep,
    onContinue: advanceToNextStep,
    onFieldChange,
    onPostalLocationChange,
    onValidationError,
    persistedFieldValues,
    postalLocationValues,
    stepperProps: machineStepperProps,
    syncToStepIndex,
  } = useLeadFormMachine(steps, { initialContext });

  const {
    advanceWithTransition,
    cancelInFlightTransition,
    isTransitionInFlight,
    wrapperProps: stepTransitionWrapperProps,
  } = useStepTransition(stepTransition);

  // Isolates this form's browser history entries from any other multistep
  // form on the same page (e.g. the care giver drawer), mirroring formId
  // usage in the legacy useMultiStepForm hook.
  const historyFormId = `lfb-${analyticsVersionId ?? formVariant}`;

  const { goBackHistory, goToFirstStepHistory, pushNextStepToHistory } =
    useLeadFormBrowserHistory({
      currentStepIndex,
      formId: historyFormId,
      isSubmitted: Boolean(state?.ok),
      steps,
      syncToStepIndex,
    });

  const {
    captureFormElement,
    formElement,
    inlineStepRegionRef,
    isEnteringTakeover,
    isTakeoverOpen,
    onCloseTakeover,
  } = useLeadFormTakeover({
    cancelInFlightTransition,
    currentStepIndex,
    enabled: Boolean(fullscreenTakeover?.enabled),
    goToFirstStepHistory,
  });

  // The machine's NEXT is deferred until the transition reaches its swap
  // point; the history push happens right after so history mirrors the
  // visible step. isEnteringTakeover skips the transition entirely so this
  // step swaps synchronously instead of racing the takeover's open animation.
  const onContinue = useCallback(() => {
    if (isEnteringTakeover) {
      advanceToNextStep();
      pushNextStepToHistory();
      return;
    }

    advanceWithTransition(() => {
      advanceToNextStep();
      pushNextStepToHistory();
    });
  }, [
    advanceToNextStep,
    advanceWithTransition,
    isEnteringTakeover,
    pushNextStepToHistory,
  ]);

  const { handleSubmit, keyboardStepActionContextValue } =
    useLeadFormKeyboardSubmit(currentStep);

  // Defers to the browser history API rather than stepping the machine
  // directly — popstate realigns the machine (see useLeadFormBrowserHistory).
  // Guarded by isTransitionInFlight so Back can't race an in-flight
  // Continue's deferred advance/pushNextStepToHistory.
  const handleBack = useCallback(() => {
    if (isTransitionInFlight()) {
      return;
    }
    goBackHistory();
  }, [goBackHistory, isTransitionInFlight]);

  const stepperProps = useMemo(
    () => ({ ...machineStepperProps, handleBack }),
    [machineStepperProps, handleBack],
  );

  // franchiseId === 0 indicates a national/corporate page with no specific franchise.
  const submittedOrganizationNumber = normalizeOfficeId(
    franchiseId !== 0
      ? franchiseId
      : Number(persistedFieldValues.franchiseNumber) || 0,
  );

  useLeadFormSubmissionTracking(
    state,
    submittedOrganizationNumber,
    persistedFieldValues,
    analyticsVersionId,
  );

  const formAnswerPlaceholders = useMemo(
    () => buildFormAnswerPlaceholders(steps, persistedFieldValues),
    [steps, persistedFieldValues],
  );

  const stepPlaceholderValues = useMemo(
    () => ({ ...placeholderValues, ...formAnswerPlaceholders }),
    [placeholderValues, formAnswerPlaceholders],
  );

  // When the server action returns field-level validation errors, push them
  // into the XState machine so input components can render them inline. The
  // honeypot error targets a field no step renders, so it stays out of the
  // machine and surfaces as the generic submission error instead.
  useEffect(() => {
    if (!state?.validationErrors) {
      return;
    }
    if (isHoneypotRejection(state.validationErrors)) {
      return;
    }
    onValidationError(
      Object.fromEntries(
        Object.entries(state.validationErrors).map(([k, v]) => [k, [v]]),
      ),
    );
  }, [state?.validationErrors, onValidationError]);

  const stepStyles = useStepStyles(
    styleClasses,
    currentStep,
    steps,
    isHeroMode,
  );

  // Pre-compute analytics user_selection per regular step so the object reference
  // is stable across renders — inline calls inside steps.map() always allocate a
  // new {}, defeating memo(LeadFormStepItemInner) on every field interaction.
  const stepUserSelections = useMemo(() => {
    return new Map(
      steps
        .filter((step): step is FormRegularStep => step._type === "step")
        .map((step) => [
          step.id,
          getStepUserSelection(
            step,
            persistedFieldValues,
            postalLocationValues,
          ),
        ]),
    );
  }, [steps, persistedFieldValues, postalLocationValues]);

  // Lets step inputs hydrate local visual state (radio checkmark, etc.) from
  // the machine on mount, so remounting StepRegion into the takeover Dialog
  // is lossless. Memoized so an unstable value doesn't defeat
  // memo(LeadFormStepItemInner).
  const persistedValuesContextValue = useMemo(
    () => ({ persistedFieldValues, postalLocationValues }),
    [persistedFieldValues, postalLocationValues],
  );

  if (isOfficeFormAndPostalCodeMissing) {
    logError({
      data: { franchise_id: String(franchiseId) },
      details: {
        tags: {
          component: logComponentName,
          reason: "office_postal_code_required_for_lead_form",
        },
      },
      message:
        "Lead form requires office postal data, but the office postal code is missing; form cannot be submitted.",
    });
    return null;
  }

  if (state?.ok) {
    // merge submitted fields so {{firstName}} resolves in thank-you Portable Text
    const firstName = persistedFieldValues.firstName;

    return (
      <ThankYou
        officeRating={officeRating}
        phoneNumber={phoneNumber}
        placeholderValues={{
          ...placeholderValues,
          ...(firstName ? { firstName } : {}),
        }}
        thankYou={thankYou}
      />
    );
  }

  // On mobile, the hero image is rendered here (outside the step grid) so it
  // persists across all steps — matching the production NationalHero behaviour
  // where the image is always visible below the form. The step-1 info panel
  // renders only the desktop image (showSmallViewportImage={false}) to avoid
  // duplication. max-xl:hidden on inactive steps (LeadFormStep) keeps the grid
  // height at the active step's height, so the hero appears within the viewport.
  const heroFirstStepPanel =
    steps[0]?.infoPanel?.mode === "hero" ? steps[0].infoPanel : undefined;

  // Never rendered inside the takeover: it exists to persist across steps
  // on mobile, which doesn't apply once steps 2+ move into a fullscreen surface.
  // The block override wins over the form-version photo, the same order as
  // the step-1 hero, so the picture stays put when that shell unmounts.
  const mobileHeroSlot = heroFirstStepPanel && (
    // Negative margins bleed the image edge-to-edge, matching the step-1
    // hero wrapper. Hidden once the desktop image in the info panel is showing.
    <div className="-mx-4 mt-8 w-[calc(100%_+_2rem)] overflow-hidden sm:-mx-10 sm:w-[calc(100%_+_5rem)] group-data-[layout=buttonStack]/lead-form:md:hidden lg:-mx-[72px] lg:w-[calc(100%_+_144px)] xl:hidden">
      <HeroImage
        breakpoint={heroFirstStepPanel.showFromBreakpoint ?? "xl"}
        heroImageLarge={
          heroImageLarge ?? heroFirstStepPanel.heroImageLarge ?? undefined
        }
        heroImageSmall={
          heroImageSmall ?? heroFirstStepPanel.heroImageSmall ?? undefined
        }
        showCareGiverCTA={heroFirstStepPanel.showCaregiverCTA ?? true}
        showLargeViewportImage={false}
      />
    </div>
  );

  const hasServerError =
    state?.ok === false &&
    (!state.validationErrors || isHoneypotRejection(state.validationErrors));

  const isSubmitPending = isPending || (isLastStep && !isTimingTokenReady);

  const stepRegion = (
    <StepRegion
      currentStep={currentStep}
      errors={errors}
      hasServerError={hasServerError}
      heroImageLarge={heroImageLarge}
      heroImageSmall={heroImageSmall}
      heroSlot={isTakeoverOpen ? null : mobileHeroSlot}
      isHeroMode={isHeroMode}
      isLastStep={isLastStep}
      isPending={isSubmitPending}
      officeRating={officeRating}
      onContinue={onContinue}
      onFieldChange={onFieldChange}
      onPostalLocationChange={onPostalLocationChange}
      onValidationError={onValidationError}
      persistentHeroMinHeightClass={
        isTakeoverOpen ? undefined : stepStyles.persistentHeroMinHeightClass
      }
      phoneNumber={phoneNumber}
      placeholderValues={stepPlaceholderValues}
      postalLocationValues={postalLocationValues}
      stepOneHero={stepOneHero}
      stepperProps={stepperProps}
      steps={steps}
      stepTransitionWrapperProps={stepTransitionWrapperProps}
      stepUserSelections={stepUserSelections}
    />
  );

  return (
    <LeadFormAnalyticsProvider
      deliveryContext={deliveryContext}
      franchiseId={franchiseId}
      pageZone={pageZone}
      // analyticsVersionId should always be provided in production so form_version_id
      // distinguishes which Sanity form version was used (e.g. "v1", "v3-urgency").
      // formVariant ("lfb") is a poor fallback but prevents a missing required prop.
      versionId={analyticsVersionId ?? formVariant}
    >
      <LeadFormWrapper
        className={stepStyles.wrapperClassName}
        heroVariant={stepOneHero?.variant}
      >
        <LeadFormPersistedValuesContext value={persistedValuesContextValue}>
          <KeyboardStepActionContext value={keyboardStepActionContextValue}>
            <LeadFormForm
              action={submitFormAction}
              onSubmit={handleSubmit}
              ref={captureFormElement}
            >
              <input
                name="organizationNumber"
                type="hidden"
                value={submittedOrganizationNumber}
              />

              <input name="leadSource" type="hidden" value={leadSource} />

              <input name="formVariant" type="hidden" value={formVariant} />

              {analyticsVersionId ? (
                <input
                  name="formVersionId"
                  type="hidden"
                  value={analyticsVersionId}
                />
              ) : null}

              <LeadFormHoneypotField />

              <LeadFormTimingTokenField
                isLastStep={isLastStep}
                onReadyChange={onTimingTokenReadyChange}
              />

              <PersistedFieldInputs
                deliveryContext={deliveryContext}
                steps={steps}
              />

              <LeadFormStepSurface
                analyticsStepName={currentStep.metricsLabel ?? currentStep.id}
                formElement={formElement}
                inlineStepRegionRef={inlineStepRegionRef}
                isTakeoverOpen={isTakeoverOpen}
                onCloseTakeover={onCloseTakeover}
                phoneNumber={phoneNumber}
                title={currentStep.name}
              >
                {stepRegion}
              </LeadFormStepSurface>
            </LeadFormForm>
          </KeyboardStepActionContext>
        </LeadFormPersistedValuesContext>
      </LeadFormWrapper>

      <Suspense fallback={null}>
        <CaregiverDrawerWithSearchParams office={office} />
      </Suspense>
    </LeadFormAnalyticsProvider>
  );
}

export { LeadForm };
